// Cálculos derivados da Cotação Rápida.
//
// FONTE ÚNICA DA VERDADE: PDF + página pública + tela interna devem usar
// `derivedQuoteFinancials` para qualquer um dos campos calculados abaixo.
// Não duplique fórmulas no JSX — adicione helper aqui se faltar.

import {
  CONNECTION_MIN_KWH,
  type ConnectionType,
} from "@/lib/solar-calculator";

const VALID_CONNECTIONS: ConnectionType[] = [
  "monofasico",
  "bifasico",
  "trifasico",
];

export interface QuoteFinancialsInput {
  bill_value: number | null;
  monthly_savings: number | null;
  consumption_kwh: number | null;
  estimated_generation_kwh: number | null;
  connection_type: string | null;
  cip_value: number | null;
  /** Tarifa em R$/kWh. Se ausente, é derivada de bill_value/consumo. */
  tariff?: number | null;
}

export interface DerivedQuoteFinancials {
  /** Conta atual informada pelo cliente (R$). */
  currentBill: number;
  /** Economia mensal real (R$). */
  monthlySavings: number;
  /** Conta projetada após instalar o solar. = max(minimumBill, currentBill − monthlySavings). */
  projectedBill: number;
  /**
   * Conta mínima de disponibilidade da concessionária (piso obrigatório).
   * = connectionMinKwh × tarifa + CIP. É o piso técnico, não a conta projetada real.
   */
  minimumBill: number;
  /** % de compensação do consumo pela geração, capado em 100. Null se não dá pra calcular. */
  compensationPct: number | null;
  /** Tarifa usada nos cálculos (R$/kWh). */
  tariffUsed: number;
  /** kWh mínimos pela conexão. */
  connectionMinKwh: number;
  /** Valor da conta equivalente ao consumo informado: consumo × tarifa. */
  equivalentBill: number;
  /** Economia anual (R$). */
  annualSavings: number;
}

/**
 * Calcula tudo que o PDF/portal/form precisam exibir sobre o lado financeiro
 * da cotação. Função pura, sem dependência de React/banco.
 */
export function derivedQuoteFinancials(
  input: QuoteFinancialsInput,
): DerivedQuoteFinancials {
  const currentBill = Number(input.bill_value || 0);
  const monthlySavings = Number(input.monthly_savings || 0);
  const consumption = Number(input.consumption_kwh || 0);
  const generation = Number(input.estimated_generation_kwh || 0);
  const cip = Number(input.cip_value || 0);

  const conn: ConnectionType = VALID_CONNECTIONS.includes(
    input.connection_type as ConnectionType,
  )
    ? (input.connection_type as ConnectionType)
    : "bifasico";
  const connectionMinKwh = CONNECTION_MIN_KWH[conn];

  // Tarifa em ordem de prioridade:
  //   1) input.tariff (tarifa explícita do tenant — solar_calc_config.default_tariff)
  //   2) derivada de conta/consumo (currentBill / consumption)
  //   3) fallback 0.9 (último recurso, com warn — tenant zero-config)
  let tariff = Number(input.tariff || 0);
  if (!(tariff > 0) && consumption > 0 && currentBill > 0) {
    tariff = currentBill / consumption;
  }
  if (!(tariff > 0)) {
    // eslint-disable-next-line no-console
    console.warn(
      "[derivedQuoteFinancials] tariff fallback 0.9 acionado — configure solar_calc_config.default_tariff",
    );
    tariff = 0.9;
  }

  const minimumBill = connectionMinKwh * tariff + cip;
  // Conta projetada real após o solar: economia derruba a conta, mas o piso de
  // disponibilidade da concessionária é intransponível.
  const projectedBill = Math.max(minimumBill, currentBill - monthlySavings);

  let compensationPct: number | null = null;
  if (consumption > 0 && generation > 0) {
    compensationPct = Math.min(100, (generation / consumption) * 100);
  }

  const equivalentBill = consumption > 0 ? consumption * tariff : 0;
  const annualSavings = monthlySavings * 12;

  return {
    currentBill,
    monthlySavings,
    projectedBill,
    minimumBill,
    compensationPct,
    tariffUsed: tariff,
    connectionMinKwh,
    equivalentBill,
    annualSavings,
  };
}

export const CONNECTION_LABELS_PT: Record<ConnectionType, string> = {
  monofasico: "monofásica",
  bifasico: "bifásica",
  trifasico: "trifásica",
};

// ─────────────── Cash flow series ───────────────

export interface CashFlowPoint {
  /** Rótulo do eixo X: "Hoje" no ano 0, "1".."25" nos demais. */
  year: string;
  /** Ano numérico (0..25). */
  yearNum: number;
  /** Saldo acumulado em R$ (negativo = ainda no vermelho). */
  valor: number;
  /** Marca o ano onde o saldo cruza zero (payback). */
  isPayback: boolean;
}

/**
 * Constrói a série de fluxo de caixa acumulado em 25 anos para a Cotação
 * Rápida. Padrão do projeto (memorial): inflação energia 6% a.a. e
 * degradação dos painéis 0,5% a.a.
 *
 * Função pura — usada pelo gráfico Recharts (página pública) e pelo gráfico
 * SVG estático (PDF). Garante que os dois desenhem a mesma curva.
 */
export function buildCashFlowSeries(
  totalValue: number,
  monthlySavings: number,
): CashFlowPoint[] {
  if (!(totalValue > 0) || !(monthlySavings > 0)) return [];

  const inflation = 0.06;
  const degradation = 0.005;

  const rows: CashFlowPoint[] = [];
  let accumulated = -totalValue;
  let paybackMarked = false;

  rows.push({
    year: "Hoje",
    yearNum: 0,
    valor: Math.round(accumulated),
    isPayback: false,
  });

  for (let year = 1; year <= 25; year++) {
    const annualSavings =
      monthlySavings *
      12 *
      Math.pow(1 + inflation, year - 1) *
      Math.pow(1 - degradation, year - 1);
    const previous = accumulated;
    accumulated += annualSavings;

    const crossesZero = previous < 0 && accumulated >= 0 && !paybackMarked;
    if (crossesZero) paybackMarked = true;

    rows.push({
      year: `${year}`,
      yearNum: year,
      valor: Math.round(accumulated),
      isPayback: crossesZero,
    });
  }

  return rows;
}
